import { useEffect, useMemo, useRef } from 'react';
import Map, { Source, Layer, Marker } from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import {
  ArrowCircleUpIcon,
  ArrowCircleDownIcon,
  ArrowUpIcon,
  TruckIcon,
  TruckTrailerIcon,
} from '@phosphor-icons/react';
import { tokens } from '../../utils/colors';

// @ts-ignore
const MAPBOX_TOKEN: string = import.meta.env.VITE_MAPBOX_TOKEN;

export interface OrderRouteStop {
  type: 'Load' | 'Unload';
  coordinates: [number, number]; // [longitude, latitude]
  name: string;
}

export interface OrderRouteMapProps {
  routeCoordinates: [number, number][]; // GeoJSON LineString coordinates
  stops: OrderRouteStop[];
  driverLocation?: [number, number] | null; // A route-snapped driver-app ping, or the raw telemetry fix
  /** Which way the vehicle is pointing, in degrees clockwise from north, as telemetry reports it. */
  driverHeading?: number | null;
  /** Where the trailer on the order is, when one is on it and the viewer may see it. */
  trailerLocation?: [number, number] | null;
  /** Which way the trailer's unit says it is heading — the truck pulling it, in practice. */
  trailerHeading?: number | null;
  /** The trailer's plate, shown under its marker so the two vehicles are told apart at a glance. */
  trailerLabel?: string | null;
  className?: string;
}

type Bounds = [[number, number], [number, number]];

/** Degrees of span below which a bounds box is a point rather than an area — roughly 50 m. */
const POINT_BOUNDS_SPAN = 0.0005;

/**
 * Ceiling for the zoom of a fit that has nothing to frame. A lone truck's bounds box is a single point, and
 * Mapbox answers a box with no extent by zooming all the way to `maxZoom`, opening the map on one street
 * corner. Capping that fit shows the vehicle its surroundings instead. Bounds with a real extent — a route —
 * are fitted exactly as before.
 */
const POINT_FIT_MAX_ZOOM = 13;

export const OrderRouteMap = ({
  routeCoordinates,
  stops,
  driverLocation,
  driverHeading,
  trailerLocation,
  trailerHeading,
  trailerLabel,
  className,
}: OrderRouteMapProps) => {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create GeoJSON for the route line
  const routeGeoJson = useMemo(() => {
    if (routeCoordinates.length < 2) return null;

    return {
      type: 'Feature' as const,
      properties: {},
      geometry: {
        type: 'LineString' as const,
        coordinates: routeCoordinates,
      },
    };
  }, [routeCoordinates]);

  // Calculate bounds to fit the entire route and stops
  const bounds = useMemo((): Bounds | null => {
    const allCoords: [number, number][] = [...routeCoordinates];
    stops.forEach((stop) => allCoords.push(stop.coordinates));
    if (driverLocation) allCoords.push(driverLocation);
    if (trailerLocation) allCoords.push(trailerLocation);

    if (allCoords.length === 0) return null;

    let minLng = allCoords[0][0];
    let maxLng = allCoords[0][0];
    let minLat = allCoords[0][1];
    let maxLat = allCoords[0][1];

    allCoords.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    return [
      [minLng, minLat],
      [maxLng, maxLat],
    ];
  }, [routeCoordinates, stops, driverLocation, trailerLocation]);

  // Fit bounds when they change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !bounds) return;

    const [[minLng, minLat], [maxLng, maxLat]] = bounds;
    const hasExtent = maxLng - minLng >= POINT_BOUNDS_SPAN || maxLat - minLat >= POINT_BOUNDS_SPAN;

    map.fitBounds(bounds, {
      padding: 50,
      duration: 500,
      // Only a box with nothing to frame needs the ceiling.
      ...(hasExtent ? {} : { maxZoom: POINT_FIT_MAX_ZOOM }),
    });
  }, [bounds]);

  // Mapbox sizes its canvas from the container when it is made, and from then on watches the window alone —
  // so a container that changes size for any other reason (a side panel dragged wider, a column that
  // reflows) would leave the canvas at its old size, with the map stopping short of its own box. Watching
  // the container instead keeps the canvas the size of the box, in both directions.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new ResizeObserver(() => mapRef.current?.getMap()?.resize());
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  // Default center (Netherlands) if no coordinates
  const defaultCenter: [number, number] = [5.2913, 52.1326];

  return (
    <div ref={containerRef} className={className ?? 'h-80 w-full rounded-lg overflow-hidden'}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: bounds ? (bounds[0][0] + bounds[1][0]) / 2 : defaultCenter[0],
          latitude: bounds ? (bounds[0][1] + bounds[1][1]) / 2 : defaultCenter[1],
          zoom: 8,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
      >
        {/* Route line */}
        {routeGeoJson && (
          <Source id="route" type="geojson" data={routeGeoJson}>
            <Layer
              id="route-line"
              type="line"
              paint={{
                'line-color': tokens.brand,
                'line-width': 4,
                'line-opacity': 0.8,
              }}
            />
          </Source>
        )}

        {/* Stop markers */}
        {stops.map((stop, index) => (
          <Marker
            key={`stop-${index}-${stop.type}`}
            longitude={stop.coordinates[0]}
            latitude={stop.coordinates[1]}
            anchor="center"
          >
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md ${
                  stop.type === 'Load' ? 'bg-positive' : 'bg-info'
                }`}
              >
                {stop.type === 'Load' ? (
                  <ArrowCircleUpIcon className="w-5 h-5 text-white" weight="bold" />
                ) : (
                  <ArrowCircleDownIcon className="w-5 h-5 text-white" weight="bold" />
                )}
              </div>
              <div className="bg-white px-2 py-1 rounded shadow-sm text-xs font-medium mt-1 max-w-24 truncate">
                {stop.name}
              </div>
            </div>
          </Marker>
        ))}

        {/* Driver location marker */}
        {driverLocation && (
          <Marker longitude={driverLocation[0]} latitude={driverLocation[1]} anchor="center">
            <div className="relative">
              <div className="absolute -inset-2 bg-brand rounded-full opacity-30 animate-ping" />
              <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                <TruckIcon className="w-5 h-5 text-white" weight="fill" />
              </div>
              {/* An arrow riding the marker's edge, swung round to the heading. The overlay is centred on the
                  marker, so turning it carries the arrow out to that side and turns the arrow itself with it —
                  upright (0°) is north, the way telemetry counts. It sits clear of the disc: an orange arrow on
                  an orange marker is no arrow at all. Drawn even while stationary, because a last known
                  direction beats none — the panel's own pill says whether the truck is moving. */}
              {typeof driverHeading === 'number' && (
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{ transform: `rotate(${driverHeading}deg)` }}
                >
                  <ArrowUpIcon
                    className="absolute -top-4 left-1/2 h-4 w-4 -translate-x-1/2 text-brand drop-shadow-sm"
                    weight="fill"
                  />
                </div>
              )}
            </div>
          </Marker>
        )}
        {/* Trailer marker. The same information as the truck's, told apart at a glance: a dark disc rather
            than the brand orange, the trailer icon rather than the truck's, and no ping — the ping is the
            truck moving, and a trailer's unit rides along with it rather than tracking on its own. */}
        {trailerLocation && (
          <Marker longitude={trailerLocation[0]} latitude={trailerLocation[1]} anchor="center">
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-8 h-8 bg-surface-inverse rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                  <TruckTrailerIcon className="w-5 h-5 text-fg-inverse" weight="fill" />
                </div>
                {typeof trailerHeading === 'number' && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    style={{ transform: `rotate(${trailerHeading}deg)` }}
                  >
                    <ArrowUpIcon
                      className="absolute -top-4 left-1/2 h-4 w-4 -translate-x-1/2 text-fg-secondary drop-shadow-sm"
                      weight="fill"
                    />
                  </div>
                )}
              </div>
              {trailerLabel && (
                <div className="bg-white px-2 py-1 rounded shadow-sm text-xs font-medium mt-1 max-w-24 truncate">
                  {trailerLabel}
                </div>
              )}
            </div>
          </Marker>
        )}
      </Map>
    </div>
  );
};
